'use client';

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Construction as ConstructionIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

const MAINTENANCE_MESSAGE_FR =
  'Notre site est actuellement en maintenance afin de vous offrir une meilleure expérience. Nous serons de retour très rapidement. Merci de votre patience.';

const MAINTENANCE_MESSAGE_EN =
  "Our website is currently undergoing maintenance to improve your experience. We'll be back very soon. Thank you for your patience.";

export default function SiteMaintenanceSettingsSection() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-mode-admin'],
    queryFn: () => apiService.getMaintenanceModeAdmin(),
  });

  const [enabled, setEnabled] = useState(false);
  const [allowedIpsText, setAllowedIpsText] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!data?.config) return;
    setEnabled(data.config.enabled);
    setAllowedIpsText((data.config.allowed_ips ?? []).join('\n'));
  }, [data?.config]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const allowed_ips = allowedIpsText
        .split(/[\n,;]+/)
        .map((line) => line.trim())
        .filter((line) => line !== '');
      return apiService.updateMaintenanceModeAdmin({ enabled, allowed_ips });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-mode-admin'] });
      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ConstructionIcon color="primary" />
        Mode maintenance site public
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Lorsque le mode maintenance est activé, le site public Sherlook affiche une page dédiée avec le logo
        Sherlook et un message en français et en anglais. Les adresses IP listées ci-dessous peuvent continuer à
        naviguer normalement.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les réglages de maintenance.
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Réglages de maintenance enregistrés.
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ p: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="warning"
              />
            }
            label={enabled ? 'Maintenance activée' : 'Maintenance désactivée'}
            sx={{ mb: 2, display: 'flex' }}
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            label="Adresses IP autorisées"
            placeholder={'Une adresse IP par ligne\nEx. 203.0.113.42\nEx. 198.51.100.10'}
            value={allowedIpsText}
            onChange={(e) => setAllowedIpsText(e.target.value)}
            helperText="Adresse IP publique vue par l’API (pas 192.168.x.x). En cas de blocage, la page maintenance affiche l’IP détectée par le serveur — copiez-la ici."
            sx={{ mb: 3 }}
          />

          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              Aperçu du message affiché
            </Typography>
            <Stack spacing={1.5}>
              <Typography variant="body2">{MAINTENANCE_MESSAGE_FR}</Typography>
              <Typography variant="body2" color="text.secondary">
                {MAINTENANCE_MESSAGE_EN}
              </Typography>
            </Stack>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </Box>

          {saveMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Impossible d&apos;enregistrer les réglages de maintenance.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
}
